import React from 'react'

// Minimal, dependency-free renderer for the legal documents in docs/legal/ —
// they only ever use #/## headings, **bold** spans, "- " bullet lists, and
// plain paragraphs, so a small hand-written parser avoids pulling in a full
// markdown library for content this regular. Builds React elements directly
// (never dangerouslySetInnerHTML), so there's no HTML-injection surface even
// though the source files are our own trusted content.

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
    }
    return <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>
  })
}

export function MarkdownDoc({ content }: { content: string }) {
  const lines = content.replace(/\r\n/g, '\n').split('\n')
  const blocks: React.ReactNode[] = []
  let paragraph: string[] = []
  let list: string[] = []

  function flushParagraph(key: string) {
    if (paragraph.length === 0) return
    blocks.push(<p key={key}>{renderInline(paragraph.join(' '), key)}</p>)
    paragraph = []
  }

  function flushList(key: string) {
    if (list.length === 0) return
    blocks.push(
      <ul key={key}>
        {list.map((item, i) => <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`)}</li>)}
      </ul>
    )
    list = []
  }

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim()
    const key = `b${idx}`

    if (line === '') {
      flushParagraph(`p${idx}`)
      flushList(`l${idx}`)
      return
    }
    if (line.startsWith('# ')) {
      flushParagraph(`p${idx}`); flushList(`l${idx}`)
      blocks.push(<h1 key={key}>{line.slice(2)}</h1>)
      return
    }
    if (line.startsWith('## ')) {
      flushParagraph(`p${idx}`); flushList(`l${idx}`)
      blocks.push(<h2 key={key}>{line.slice(3)}</h2>)
      return
    }
    if (line.startsWith('- ')) {
      flushParagraph(`p${idx}`)
      list.push(line.slice(2))
      return
    }
    flushList(`l${idx}`)
    paragraph.push(line)
  })
  flushParagraph('p-end')
  flushList('l-end')

  return <div className="legal-doc">{blocks}</div>
}
